import { Button, DataTable } from 'shared/components/ui';
import React, { useState, useCallback, useRef } from 'react';
import { useGastosFijos } from '../hooks/useGastosFijos';
import { GRUPOS_ORDEN, GRUPOS_LABELS } from '../utils/constantesGastos';
import { validarMontoGasto } from '../utils/validacionGastos';
import { exportGastosFijos } from '../utils/excelGastosEntradas';
import './GastosFijos.css';

const MENSAJES_ERROR = {
  empty: 'Ingresá un valor',
  not_numeric: 'El valor debe ser numérico',
  negative: 'El valor no puede ser negativo',
  too_many_decimals: 'Máximo 2 decimales',
  exceeds_maximum: 'El valor excede el máximo permitido',
  save_failed: 'Error al guardar el valor',
};

const formatMonto = (valor) => {
  const num = Number(valor) || 0;
  return num.toLocaleString('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/**
 * Celda editable de un gasto fijo individual.
 */
const CeldaGasto = ({ item, mes, anio, guardarValor, saving }) => {
  const [editing, setEditing] = useState(false);
  const [valorLocal, setValorLocal] = useState('');
  const [errorInline, setErrorInline] = useState(null);
  const valorOriginalRef = useRef(null);

  const handleFocus = () => {
    setEditing(true);
    setErrorInline(null);
    const raw = item.valor !== undefined && item.valor !== null ? String(item.valor) : '0';
    setValorLocal(raw);
    valorOriginalRef.current = raw;
  };

  const handleChange = (e) => {
    setValorLocal(e.target.value);
    if (errorInline) {
      setErrorInline(null);
    }
  };

  const handleGuardar = async () => {
    setEditing(false);

    const valorTrimmed = valorLocal.trim();

    // Si no cambió, no hacer nada
    if (valorTrimmed === valorOriginalRef.current) {
      return;
    }

    // Si quedó vacío, restaurar al original sin error
    if (valorTrimmed === '') {
      setValorLocal(valorOriginalRef.current);
      return;
    }

    // Validar
    const validacion = validarMontoGasto(valorTrimmed);
    if (!validacion.valid) {
      setErrorInline(MENSAJES_ERROR[validacion.error] || 'Valor inválido');
      return;
    }

    // Guardar
    const resultado = await guardarValor(item.idCategoria, valorTrimmed, mes, anio);
    if (resultado && !resultado.valid) {
      setErrorInline(MENSAJES_ERROR[resultado.error] || 'Error al guardar');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  const displayValue = editing ? valorLocal : formatMonto(item.valor);

  return (
    <td className="gastos-fijos-input-cell">
      <input
        type="text"
        className={`gastos-fijos-input${errorInline ? ' input-error' : ''}`}
        value={displayValue}
        onFocus={handleFocus}
        onChange={handleChange}
        onBlur={handleGuardar}
        onKeyDown={handleKeyDown}
        disabled={saving}
        aria-label={`Valor de ${item.nombre}`}
        inputMode="decimal"
      />
      {errorInline && (
        <div className="gastos-fijos-error-msg" role="alert">
          {errorInline}
        </div>
      )}
    </td>
  );
};

const GastosFijos = ({ mes, anio, onToast }) => {
  const {
    gastos,
    subtotales,
    totalGeneral,
    loading,
    saving,
    buscar,
    guardarValor,
  } = useGastosFijos({ onToast });

  const [buscado, setBuscado] = useState(false);

  const handleBuscar = useCallback(() => {
    setBuscado(true);
    buscar(mes, anio);
  }, [buscar, mes, anio]);

  const handleExportar = useCallback(async () => {
    try {
      exportGastosFijos(gastos, subtotales, totalGeneral, mes, anio);
    } catch (err) {
      console.error('Error al exportar gastos fijos:', err);
      if (onToast) {
        onToast({ type: 'error', message: 'Error al generar el archivo de exportación' });
      }
    }
  }, [gastos, subtotales, totalGeneral, mes, anio, onToast]);

  const tieneData = GRUPOS_ORDEN.some(
    (grupo) => gastos[grupo] && gastos[grupo].length > 0
  );

  return (
    <div className="gastos-fijos-container" role="region" aria-label="Gastos fijos mensuales">
      {/* Toolbar */}
      <div className="gastos-fijos-toolbar">
        <Button
          className="btn-buscar"
          onClick={handleBuscar}
          disabled={loading}
          aria-label="Buscar gastos fijos del período"
        >
          {loading ? 'Buscando...' : 'Buscar'}
        </Button>
        <Button
          className="btn-exportar"
          onClick={handleExportar}
          disabled={loading || !tieneData}
          aria-label="Exportar gastos fijos a Excel"
        >
          Exportar Excel
        </Button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="gastos-fijos-loading" role="status" aria-live="polite">
          <div className="gastos-fijos-spinner" aria-hidden="true"></div>
          <span>Cargando gastos fijos...</span>
        </div>
      )}

      {/* Tabla */}
      {!loading && buscado && tieneData && (
        <div className="cont-tabla-wrap" role="region" aria-label="Tabla de gastos fijos">
          <DataTable className="gastos-fijos-tabla" aria-label="Gastos fijos por centro">
            <thead>
              <tr>
                <th scope="col" className="gastos-fijos-th">Categoría</th>
                <th scope="col" className="gastos-fijos-th gastos-fijos-th-valor">Valor</th>
              </tr>
            </thead>
            <tbody>
              {GRUPOS_ORDEN.map((grupo) => (
                <React.Fragment key={grupo}>
                  {/* Header del grupo */}
                  <tr className="gastos-fijos-grupo-header">
                    <td colSpan={2}>{GRUPOS_LABELS[grupo]}</td>
                  </tr>

                  {/* Filas de categorías */}
                  {(gastos[grupo] || []).map((item) => (
                    <tr key={item.idCategoria} className="gastos-fijos-fila">
                      <td className="gastos-fijos-nombre">{item.nombre}</td>
                      <CeldaGasto
                        item={item}
                        mes={mes}
                        anio={anio}
                        guardarValor={guardarValor}
                        saving={saving}
                      />
                    </tr>
                  ))}

                  {/* Subtotal del grupo */}
                  <tr className="gastos-fijos-subtotal">
                    <td>Subtotal {GRUPOS_LABELS[grupo]}</td>
                    <td className="gastos-fijos-subtotal-valor">
                      ${formatMonto(subtotales[grupo])}
                    </td>
                  </tr>
                </React.Fragment>
              ))}

              {/* Total general */}
              <tr className="gastos-fijos-total">
                <td>Total General</td>
                <td className="gastos-fijos-total-valor">
                  ${formatMonto(totalGeneral)}
                </td>
              </tr>
            </tbody>
          </DataTable>
        </div>
      )}

      {/* Estado vacío */}
      {!loading && buscado && !tieneData && (
        <div className="gastos-fijos-empty" role="status" aria-live="polite">
          <p>No se encontraron gastos fijos para el período seleccionado.</p>
          <p>Presioná "Buscar" para cargar datos.</p>
        </div>
      )}

      {/* Estado inicial (antes de buscar) */}
      {!loading && !buscado && (
        <div className="gastos-fijos-empty" role="status">
          <p>Seleccioná un período y presioná "Buscar" para cargar los gastos fijos.</p>
        </div>
      )}
    </div>
  );
};

export default GastosFijos;
